/**
 * A surface's own depth material, which cuts the surface's whole ALPHA as Godot's depth draws do:
 * the albedo alpha with the fade in it, the texture and the vertex colour
 * (`scene_forward_clustered.glsl:1251-1432`). three's depth materials read the texture alone.
 */
import { composeInjections, injectProgram, stateInjectionKey, } from '../materialProgramInputs';
import { editedProgram } from '../shaderPatch/chunkPatch';
import { drawDepthInjections } from './depthInjections';
/**
 * Seeds the alpha with `opacity` and multiplies the vertex colour in, as three's colour programs do.
 * three's distance fragment declares no `opacity`, so a program that reads it fails to link.
 */
const SURFACE_ALPHA = {
    cacheKey: 'godot-surface-alpha',
    onBeforeCompile(shader) {
        editedProgram(shader, {
            vertex: [
                { three: '#include <common>', godot: '#include <common>\n#include <color_pars_vertex>' },
                { three: '#include <begin_vertex>', godot: '#include <color_vertex>\n#include <begin_vertex>' },
            ],
            fragment: [
                {
                    three: '#include <common>',
                    godot: '#ifdef DISTANCE\nuniform float opacity;\n#endif\n#include <common>\n#include <color_pars_fragment>',
                },
                {
                    three: '#include <map_fragment>',
                    godot: 'diffuseColor.a = opacity;\n#include <map_fragment>\n#include <color_fragment>',
                },
            ],
        }, 'depth material surface alpha');
    },
};
/** Each surface's depth materials, one per three material it copies. Disposed with the surface. */
// eslint-disable-next-line no-restricted-syntax -- a surface's dispose deletes its entry
const depthCopies = new WeakMap();
/**
 * The copy of three's `base` depth or distance material that draws `surface`. It keeps the base's
 * packing, and its light for a distance material.
 */
export function surfaceDepthMaterial(surface, base) {
    const copies = copiesOf(surface);
    let copy = copies.get(base);
    if (!copy) {
        copy = base.clone();
        copy.alphaHash = surface.alphaHash;
        copy.userData = surface.userData;
        injectProgram(copy, surfaceAlphaOf(copy));
        copies.set(base, copy);
    }
    return copy;
}
function copiesOf(surface) {
    let copies = depthCopies.get(surface);
    if (!copies) {
        const created = new Map();
        surface.addEventListener('dispose', () => {
            for (const copy of created.values())
                copy.dispose();
            depthCopies.delete(surface);
        });
        depthCopies.set(surface, created);
        copies = created;
    }
    return copies;
}
/**
 * Gives `depth` the alpha state of `surface` for one draw, cut at `alphaTest`. A change three keys
 * no program on, a slot's presence, the hash, the edge ramp, the size of a points surface or a
 * particle draw, recompiles it.
 */
export function syncSurfaceDepth(depth, surface, alphaTest) {
    const source = surface;
    const recompiles = stateInjectionKey(depth) !== stateInjectionKey(surface) ||
        depth.vertexColors !== surface.vertexColors ||
        !depth.map !== !source.map ||
        !depth.alphaMap !== !source.alphaMap ||
        !sameInjections(drawDepthInjections(depth.userData), drawDepthInjections(surface.userData));
    depth.map = source.map ?? null;
    depth.alphaMap = source.alphaMap ?? null;
    depth.opacity = surface.opacity;
    depth.vertexColors = surface.vertexColors;
    depth.alphaTest = alphaTest;
    depth.userData = surface.userData;
    depth.clippingPlanes = surface.clippingPlanes;
    depth.clipIntersection = surface.clipIntersection;
    depth.clipShadows = surface.clipShadows;
    if (!recompiles)
        return;
    depth.alphaHash = surface.alphaHash;
    injectProgram(depth, surfaceAlphaOf(depth));
    depth.needsUpdate = true;
}
/** The depth program's own injection and the surface's alpha cut, among the draw's own injections. */
function surfaceAlphaOf(depth) {
    const { beforeCut, afterCut } = drawDepthInjections(depth.userData);
    return composeInjections([depth.depthInjection, beforeCut, SURFACE_ALPHA, afterCut]);
}
function sameInjections(a, b) {
    return a.beforeCut === b.beforeCut && a.afterCut === b.afterCut;
}
//# sourceMappingURL=surfaceDepthMaterial.js.map